import { useEffect, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import {
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Navigation,
  Linkedin,
  Twitter
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { openWhatsApp } from '@/lib/whatsapp';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { formatOpeningHoursDayRange, hasOpeningHoursData } from '@/lib/openingHours';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import './styles.css';
import { openStoreDirections } from '@/lib/storeMapUrls';

const Contact = () => {
  const { config } = useAppData();
  const allStores = config.stores || [];
  const mainStore = allStores[0];
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  const buttonColor = colors.button;
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const formatOpeningHours = (openingHours: any) => {
    const days = [
      { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
      { key: 'tuesday', label: 'Terça-feira', short: 'Ter' },
      { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
      { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
      { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
      { key: 'saturday', label: 'Sábado', short: 'Sáb' },
      { key: 'sunday', label: 'Domingo', short: 'Dom' },
    ];

    return days.map((day) => ({
      day: day.label,
      short: day.short,
      hours: formatOpeningHoursDayRange(openingHours, day.key),
    }));
  };

  const renderStoreCard = (store: any) => {
    const distance = getDistanceToStore(userLocation, store);

    return (
      <article className="wl4-store-card">
        <div className="wl4-store-card-grid">
          {store.frontage && (
            <div className="wl4-store-card-media">
              <img src={store.frontage} alt={`Fachada ${store.company}`} loading="lazy" />
            </div>
          )}

          <div className="wl4-store-card-body">
            {store.place?.city && (
              <p className="wl4-eyebrow mb-1">
                {store.place.city}{store.place?.state ? ` — ${store.place.state}` : ''}
              </p>
            )}
            <h2 className="wl4-h2">{store.company}</h2>
            <p className="wl4-lead mt-2 text-sm">Sua próxima experiência automotiva começa aqui.</p>

            <div className="wl4-contact-methods">
              {store.phone?.ios && (
                <a href={store.phone.ios} className="wl4-contact-method">
                  <div className="wl4-contact-method-icon">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="wl4-contact-method-label">Telefone</p>
                    <p className="wl4-contact-method-value">{store.phone.formatted}</p>
                    <p className="wl4-contact-method-hint">Ligar agora</p>
                  </div>
                </a>
              )}

              {store.whatsapp?.ios && (
                <button
                  type="button"
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                  className="wl4-contact-method"
                >
                  <div className="wl4-contact-method-icon">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="wl4-contact-method-label">WhatsApp</p>
                    <p className="wl4-contact-method-value">{store.whatsapp.formatted}</p>
                    <p className="wl4-contact-method-hint">Enviar mensagem</p>
                  </div>
                </button>
              )}

              {store.place && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!userLocation) {
                      const location = await requestUserLocation();
                      if (location) setUserLocation(location);
                    }
                    const place = store.place;
                    openStoreDirections(place);
                  }}
                  className="wl4-contact-method"
                >
                  <div className="wl4-contact-method-icon">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="wl4-contact-method-label">Localização</p>
                    <p className="wl4-contact-method-value line-clamp-2">
                      {store.place?.address || 'Endereço da empresa'}
                    </p>
                    {distance !== null && (
                      <p className="wl4-contact-method-hint flex items-center gap-1 mt-1">
                        <Navigation className="h-3 w-3" />
                        {formatDistance(distance)}
                      </p>
                    )}
                    <p className="wl4-contact-method-hint">Como chegar</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>

        {hasOpeningHoursData(store.openingHours) && (
          <div className="wl4-store-hours">
            <div className="flex items-center gap-3 mb-4">
              <div className="wl4-info-card-icon !mb-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--wl4-text)]">Horários de funcionamento</h3>
                <p className="text-sm text-[var(--wl4-muted)]">Confira nossos horários de atendimento</p>
              </div>
            </div>
            <div className="wl4-hours-grid">
              {formatOpeningHours(store.openingHours).map((schedule) => (
                <div key={schedule.day} className="wl4-hour-chip">
                  <p className="wl4-hour-chip-day">{schedule.short}</p>
                  <p className={`wl4-hour-chip-time ${schedule.hours === 'Fechado' ? 'is-closed' : ''}`}>
                    {schedule.hours}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    );
  };

  const socialLinks = [
    { key: 'instagram', url: config.socialNetworks?.instagram, Icon: Instagram, label: 'Instagram' },
    { key: 'facebook', url: config.socialNetworks?.facebook, Icon: Facebook, label: 'Facebook' },
    { key: 'youtube', url: config.socialNetworks?.youtube, Icon: Youtube, label: 'YouTube' },
    { key: 'linkedin', url: config.socialNetworks?.linkedin, Icon: Linkedin, label: 'LinkedIn' },
    { key: 'twitter', url: config.socialNetworks?.twitter, Icon: Twitter, label: 'X (Twitter)' },
  ].filter((s) => !!s.url);

  return (
    <div className="layout-4 min-h-screen bg-[var(--wl4-bg)]" style={themeStyle}>
      <Header config={config} />

      <div className="wl4-page-banner">
        <div className="wl4-container">
          <p className="wl4-eyebrow mb-2">Fale conosco</p>
          <h1>Contato</h1>
          <p className="mt-2 max-w-lg">Estamos prontos para ajudá-lo a encontrar o veículo ideal.</p>
        </div>
      </div>

      <section className="wl4-section bg-white !pt-8 !pb-6">
        <div className="wl4-container">
          <div className="wl4-info-cards">
          <div className="wl4-info-card">
            <div className="wl4-info-card-icon">
              <Phone className="h-5 w-5" />
            </div>
            <h3>Telefone</h3>
            <p>
              <a href={mainStore.phone?.ios} className="hover:underline font-medium" style={{ color: buttonColor }}>
                {mainStore.phone?.formatted}
              </a>
            </p>
          </div>
          <div className="wl4-info-card">
            <div className="wl4-info-card-icon">
              <MapPin className="h-5 w-5" />
            </div>
            <h3>Endereço</h3>
            <p>{mainStore.place?.address || 'Consulte nossos endereços abaixo'}</p>
          </div>
          <div className="wl4-info-card">
            <div className="wl4-info-card-icon">
              <Clock className="h-5 w-5" />
            </div>
            <h3>Horário de Funcionamento</h3>
            <p>
              {hasOpeningHoursData(mainStore?.openingHours)
                ? formatOpeningHours(mainStore.openingHours)
                    .filter((s) => s.hours !== 'Fechado')
                    .slice(0, 2)
                    .map((s) => `${s.day}: ${s.hours}`)
                    .join(' | ') || 'Consulte horários abaixo'
                : 'Consulte horários abaixo'}
            </p>
          </div>
          </div>
        </div>
      </section>

      <div className="wl4-page-body">
        <div className="wl4-container space-y-12 md:space-y-16">
          {config.about && (
            <section className="wl4-about-card">
              <div className="flex flex-col sm:flex-row items-start gap-5 mb-6">
                {config.icon?.light && (
                  <img
                    src={config.icon.light}
                    alt={config.name}
                    className="h-14 w-auto object-contain rounded-lg flex-shrink-0"
                  />
                )}
                <div>
                  <p className="wl4-eyebrow mb-1">Sobre nós</p>
                  <h2 className="wl4-h2">Sobre a {config.name}</h2>
                </div>
              </div>
              <div className="text-[var(--wl4-muted)] leading-relaxed whitespace-pre-line">
                {config.about}
              </div>
            </section>
          )}

          {allStores.length >= 1 && (
            <section className="space-y-6">
              <div>
                <p className="wl4-eyebrow">Nossas lojas</p>
                <h2 className="wl4-h2 mt-1">Onde estamos</h2>
              </div>
              <div className="space-y-6">
                {allStores.map((store, index) => (
                  <div key={store._id || index}>{renderStoreCard(store)}</div>
                ))}
              </div>
            </section>
          )}

          {(socialLinks.length > 0 || config.email) && (
            <section className="text-center py-4">
              <p className="wl4-eyebrow">Redes sociais</p>
              <h2 className="wl4-h2 mt-1">Conecte-se conosco</h2>
              <p className="wl4-lead mt-2 max-w-xl mx-auto text-sm">
                Siga nossas redes e fique por dentro das novidades e promoções
              </p>

              <div className="wl4-social-grid">
                {socialLinks.map(({ key, url, Icon, label }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wl4-social-btn"
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </a>
                ))}
                {config.email && (
                  <a href={`mailto:${config.email}`} className="wl4-social-btn">
                    <Mail className="h-4 w-4" />
                    {config.email}
                  </a>
                )}
              </div>
            </section>
          )}

          {(hasGooglePlayLink || hasAppStoreLink) && (
            <section className="wl4-app-strip">
              <div className="wl4-app-strip-inner">
                <h2 className="text-2xl md:text-3xl font-bold mb-3">Baixe nosso app</h2>
                <p className="text-white/80 mb-8 max-w-xl mx-auto text-sm md:text-base">
                  Estoque, financiamentos e promoções na palma da sua mão
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  {hasGooglePlayLink && (
                    <a href={config.googlePlay.url} target="_blank" rel="noopener noreferrer">
                      <img src={googlePlay} alt="Disponível no Google Play" className="h-12 w-auto" />
                    </a>
                  )}
                  {hasAppStoreLink && (
                    <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
                      <img src={appStore} alt="Baixar na App Store" className="h-12 w-auto" />
                    </a>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      <Footer config={config} />
    </div>
  );
};

export default Contact;
